<template>
  <RvRentalsPage
    v-if="data?.destinationDetails?.RouteDetails?.PointOfInterest"
    :point-of-interest-id="data?.destinationDetails?.RouteDetails?.PointOfInterest?.Id"
    :location="{
      title: destinationLocationTitles.title,
      titleShort: destinationLocationTitles.titleShort,
      titleWithPreposition: destinationLocationTitles.title,
      titleNearby: destinationLocationTitles.titleNearby,
      destinationType: (data?.destinationDetails?.RouteDetails?.PointOfInterest?.Component as POINT_OF_INTEREST_TYPE) ?? undefined,
      regionType,
      stats: data?.destinationDetails?.RouteDetails?.PointOfInterest?.Stats ?? undefined,
      coordinates: {
        latitude: data?.destinationDetails?.RouteDetails?.PointOfInterest?.Coordinates?.Latitude ?? 0,
        longitude: data?.destinationDetails?.RouteDetails?.PointOfInterest?.Coordinates?.Longitude ?? 0,
      },
      name: data?.destinationDetails?.RouteDetails?.PointOfInterest?.Name ?? '',
      nameEn: destinationComponent?.Names?.en ?? '',
      regionId: regionComponent?.Id ?? '',
      regionCode: regionComponent?.NameAbbreviation ?? '',
      regionName: regionComponent?.Names?.en ?? '',
      countryCode: countryComponent?.NameAbbreviation ?? '',
      bounds: data?.destinationDetails?.RouteDetails?.PointOfInterest?.Bounds ?? undefined,
    }"
    :breadcrumb-items="breadcrumbItems"
    :faqs="data?.destinationDetails?.RouteDetails?.FAQs?.map((faq) => ({
      Id: faq.Id,
      Question: faq.Question ?? '',
      Answer: faq.Answer ?? '',
      Source: faq.Source ?? undefined,
    }))"
    :content-blocks="data?.destinationDetails?.ContentBlocks ?? []"
    :nearby-locations="data?.destinationDetails.Nearby?.Results ?? []"
    :available-keywords="data?.destinationDetails?.RouteDetails?.PointOfInterest?.KeywordSlugs ?? []"
  />
</template>

<script setup lang="ts">
import { ROUTE_BASE_NAME } from '~/constants'
import type { POINT_OF_INTEREST_TYPE } from '~/constants/point-of-interest'
import type { SupportedLocales } from '#shared/types/locales'
import type { Breadcrumb } from '~/components/_tailwind/breadcrumb/types'
import { handleExitIntent } from '#shared/lib/exit-intent'

definePageMeta({
  middleware: [
    'destination-path',
    'keyword',
  ],
  layout: 'static',
  scrollToTop: true,
  headerSearchFormVisibility: 'dynamic',

  i18n: {
    paths: {
      en: '/rv-rentals/[[keyword]]/destination/[...destinationPath]',
      fr: '/locations-vr/[[keyword]]/destination/[...destinationPath]',
    },
  },
})

const route = useRoute()
const keyword = computed(() => Array.isArray(route.params.keyword) ? route.params.keyword.join(' ') : route.params.keyword)

const { locale, availableLocales, te, t } = useI18n()

const localePath = useLocalePath()
const { $gis, $captureError } = useNuxtApp()

const { getDestinationDetails, getComponentByKey } = usePointOfInterest()

if (route.path?.includes('destination/destination')) {
  await navigateTo(localePath({
    name: ROUTE_BASE_NAME.rv_rentals_destination,
    params: {
      destinationPath: route.params?.destinationPath,
    },
  }), { redirectCode: 301 })
}

const requestKey = computed(() => `rv-rentals-destination-${Object.values(route.params).flat().join('-')}-${locale.value}`)
const { data, error } = await useAsyncData(requestKey, async () => {
  const destinationDetails = await getDestinationDetails({ action: 'rv-rental' })

  if (!destinationDetails?.RouteDetails?.PointOfInterest) {
    await navigateTo(localePath(ROUTE_BASE_NAME.rv_rentals), { redirectCode: 301 })
    return { destinationDetails }
  }

  // Redirect to canonical path by locale
  const destinationPath = destinationPathString(route.params.destinationPath)
  const localizedCanonicalPath = destinationDetails.RouteDetails.PointOfInterest?.CanonicalPaths?.[locale.value]
  if (localizedCanonicalPath && localizedCanonicalPath !== destinationPath) {
    const newPath = localePath({
      name: ROUTE_BASE_NAME.rv_rentals_destination,
      params: {
        keyword: route.params?.keyword,
        destinationPath: destinationPathSegments(localizedCanonicalPath),
      },
    })
    await navigateTo(newPath, { redirectCode: 301 })
    return { destinationDetails }
  }

  const googlePlaceId = destinationDetails.RouteDetails.PointOfInterest.Attributes?.GooglePlaceId as string | undefined

  const gisData = googlePlaceId
    ? await $gis('/Place/Details', {
        query: {
          PlaceId: googlePlaceId,
          Language: locale.value,
        },
      })
    : destinationDetails?.RouteDetails?.PointOfInterest?.FullName
      ? await $gis('/Geocoding/Geocode', {
          query: {
            Address: destinationDetails.RouteDetails.PointOfInterest.FullName,
            Language: locale.value,
          },
        })
      : undefined

  return {
    destinationDetails,
    gisData,
  }
})

if (error.value) {
  $captureError(error.value)
  throw createError({ ...error.value, fatal: true })
}

if (!data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.CanonicalPaths?.[locale.value]) {
  throw createError({
    status: 404,
    statusText: 'Not Found',
    fatal: true,
  })
}

const destinationComponent = computed(() => {
  return getComponentByKey(data.value?.destinationDetails?.RouteDetails?.Breadcrumbs, data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Component as POINT_OF_INTEREST_TYPE | undefined)
})

const regionComponent = computed(() => {
  return getComponentByKey(data.value?.destinationDetails?.RouteDetails?.Breadcrumbs, 'Region')
})

const regionType = computed(() => {
  if (typeof data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Attributes?.RegionType === 'string') {
    return data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Attributes?.RegionType
  }

  return undefined
})

const countryComponent = computed(() => {
  return getComponentByKey(data.value?.destinationDetails?.RouteDetails?.Breadcrumbs, 'Country')
})

const destinationLocationTitles = computed(() => {
  const poi = data.value?.destinationDetails?.RouteDetails?.PointOfInterest
  if (!poi) {
    return { title: '', titleNearby: '' }
  }
  const base = {
    locale: locale.value,
    pointOfInterestId: poi.Id,
    name: poi.Name,
    component: poi.Component,
    parentName: typeof poi.ParentAttributes?.Abbreviation === 'string' ? poi.ParentAttributes.Abbreviation : poi.ParentName,
    grammarRules: poi.Attributes?.GrammarRules,
  }
  return {
    title: pointOfInterestLocationNameWithPreposition(base),
    titleShort: pointOfInterestLocationNameWithPreposition({ ...base, parentName: undefined }),
    titleNearby: pointOfInterestLocationNameWithPreposition({ ...base, useNearby: true }),
  }
})

/**
 * Set search location fields
 *
 * The header renders before this page, so the search form only learns about the
 * destination once the client has taken over. Setting it any earlier would leave the
 * server describing a location-less search while the client describes this one.
 */
const searchData = useSearchData()
const { updateSearchFormLocation } = useSearchForm()
const { prePopulateSearchLocation } = useLocationPredictions()

onMounted(() => {
  const placeId = data.value?.gisData?.placeId
  const locationText = data.value?.gisData?.name ?? ''

  prePopulateSearchLocation(locationText)

  if (placeId) {
    updateSearchFormLocation({
      placeId,
      text: locationText,
    })
    searchData.value.location = data.value?.gisData
  }
})

const breadcrumbItems = computed(() => {
  const items: Breadcrumb[] = []
  data.value?.destinationDetails?.RouteDetails?.Breadcrumbs?.forEach((x) => {
    const canonicalPath = x.CanonicalPaths?.[locale.value]
    const name = x.Names?.[locale.value]

    if (!name) return

    items.push({
      text: name,
      to: localePath({
        name: ROUTE_BASE_NAME.rv_rentals_destination,
        params: {
          keyword: route.params?.keyword,
          destinationPath: destinationPathSegments(canonicalPath),
        },
      }),
    })
  })

  return items
})

/**
   * Set lang switcher value
   */
const setI18nParams = useSetI18nParams()
const i18nParams: Record<SupportedLocales, { keyword: string, destinationPath: string[] } | undefined> = { en: undefined, fr: undefined }
availableLocales.forEach((localeCode) => {
  const canonicalPath = data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.CanonicalPaths?.[localeCode]

  let localizedKeyword = ''
  if (keyword.value) {
    localizedKeyword = te(`keyword.${keyword.value}.slug.${localeCode}`)
      ? t(`keyword.${keyword.value}.slug.${localeCode}`)
      : keyword.value
  }

  i18nParams[localeCode] = {
    keyword: localizedKeyword,
    destinationPath: destinationPathSegments(canonicalPath),
  }
})

setI18nParams(i18nParams)

onBeforeRouteLeave(() => {
  setI18nParams({})
})

/**
 * Exit intent tracking
 */
const { logCustomEvent } = await useBraze()
const { isLoggedIn } = useAuthentication()
let disableExitIntent: () => void

onMounted(() => {
  if (data.value?.destinationDetails?.RouteDetails) {
    trackPointOfInterestViewed({
      pageSource: 'rv-rentals',
      pageNumber: undefined, // This is currently always returning undefined
      component: data.value.destinationDetails?.RouteDetails?.PointOfInterest?.Component ?? '',
      action: 'RVRentals',
      pointOfInterestId: data.value.destinationDetails?.RouteDetails?.PointOfInterest?.Id ?? '',
    })
  }

  if (!isLoggedIn.value && locale.value === 'en') {
    const { disable } = handleExitIntent({
      eventName: 'ExitIntentRvRentalsDestinationPage',
      properties: {
        destinationName: data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Name,
      },
      logEvent: logCustomEvent,
    })
    disableExitIntent = disable
  }
})

onBeforeUnmount(() => {
  disableExitIntent?.()
})
</script>

<i18n src="~~/i18n/locales/common/search/keywords.json" lang="json" />
